import { openDB, type IDBPDatabase } from 'idb';
import { api, type PitchCount, type Game, type Player, type PitchRule, type RestStatus } from './api';

const DB_NAME = 'pitch-counter';
const DB_VERSION = 2; // Bump version for new stores

interface PendingAction {
  id: string;
  type: 'upsert_pitch' | 'update_pitch' | 'delete_pitch' | 'create_game' | 'add_pitch_event' | 'remove_pitch_event';
  data: unknown;
  timestamp: number;
  retries?: number;
}

interface SyncState {
  isOnline: boolean;
  pendingCount: number;
  lastSync: number | null;
}

type SyncListener = (state: SyncState) => void;

class OfflineService {
  private db: IDBPDatabase | null = null;
  private state: SyncState = {
    isOnline: navigator.onLine,
    pendingCount: 0,
    lastSync: null,
  };
  private listeners: Set<SyncListener> = new Set();
  private syncInProgress = false;

  async init(): Promise<void> {
    this.db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Pending actions queue
        if (!db.objectStoreNames.contains('pending')) {
          db.createObjectStore('pending', { keyPath: 'id' });
        }

        // Cached data
        if (!db.objectStoreNames.contains('games')) {
          db.createObjectStore('games', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('players')) {
          db.createObjectStore('players', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('pitches')) {
          db.createObjectStore('pitches', { keyPath: 'id' });
        }

        // Pitch rules and rest status (added in version 2)
        if (!db.objectStoreNames.contains('pitch-rules')) {
          db.createObjectStore('pitch-rules', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('player-rest-status')) {
          db.createObjectStore('player-rest-status', { keyPath: 'player_id' });
        }
      },
    });

    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());

    // Update pending count
    await this.updatePendingCount();
  }

  private async handleOnline(): Promise<void> {
    this.setState({ ...this.state, isOnline: true });
    await this.syncPending();
  }

  private handleOffline(): void {
    this.setState({ ...this.state, isOnline: false });
  }

  private setState(newState: SyncState): void {
    this.state = newState;
    this.listeners.forEach((listener) => listener(this.state));
  }

  getState(): SyncState {
    return this.state;
  }

  subscribe(listener: SyncListener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  private async updatePendingCount(): Promise<void> {
    if (!this.db) return;
    const count = await this.db.count('pending');
    this.setState({ ...this.state, pendingCount: count });
  }

  // Queue an action for later sync
  async queueAction(type: PendingAction['type'], data: unknown): Promise<void> {
    if (!this.db) return;

    const action: PendingAction = {
      id: crypto.randomUUID(),
      type,
      data,
      timestamp: Date.now(),
    };

    await this.db.put('pending', action);
    await this.updatePendingCount();

    // Try to sync immediately if online
    if (this.state.isOnline) {
      await this.syncPending();
    }
  }

  // Sync pending actions with server
  async syncPending(): Promise<void> {
    if (!this.db || this.syncInProgress || !this.state.isOnline) return;

    this.syncInProgress = true;

    try {
      const pending = await this.db.getAll('pending');

      for (const action of pending) {
        try {
          await this.processAction(action);
          await this.db.delete('pending', action.id);
        } catch (error) {
          console.error('Failed to sync action:', action, error);
          const retries = (action.retries ?? 0) + 1;
          if (retries >= 5) {
            console.warn('Dropping action after 5 failed retries:', action);
            await this.db.delete('pending', action.id);
          } else {
            await this.db.put('pending', { ...action, retries });
          }
        }
      }

      this.setState({
        ...this.state,
        lastSync: Date.now(),
      });
    } finally {
      this.syncInProgress = false;
      await this.updatePendingCount();
    }
  }

  private async processAction(action: PendingAction): Promise<void> {
    switch (action.type) {
      case 'upsert_pitch': {
        const { gameId, playerId, count } = action.data as {
          gameId: string;
          playerId: string;
          count: number;
        };
        await api.upsertPitchCount(gameId, { player_id: playerId, count });
        break;
      }
      case 'update_pitch': {
        const { id, count } = action.data as { id: string; count: number };
        await api.updatePitchCount(id, count);
        break;
      }
      case 'delete_pitch': {
        const { id } = action.data as { id: string };
        await api.deletePitchCount(id);
        break;
      }
      case 'create_game': {
        const data = action.data as Parameters<typeof api.createGame>[0];
        await api.createGame(data);
        break;
      }
      case 'add_pitch_event': {
        const { gameId, playerId, inningId } = action.data as {
          gameId: string;
          playerId: string;
          inningId?: string;
        };
        await api.addPitchEvent(gameId, { player_id: playerId, inning_id: inningId });
        break;
      }
      case 'remove_pitch_event': {
        const { gameId, playerId } = action.data as {
          gameId: string;
          playerId: string;
        };
        await api.removeLatestPitchEvent(gameId, playerId);
        break;
      }
    }
  }

  // Cache data for offline access
  async cacheGame(game: Game): Promise<void> {
    if (!this.db) return;
    await this.db.put('games', game);
  }

  async cachePlayer(player: Player): Promise<void> {
    if (!this.db) return;
    await this.db.put('players', player);
  }

  async cachePitch(pitch: PitchCount): Promise<void> {
    if (!this.db) return;
    await this.db.put('pitches', pitch);
  }

  async getCachedGame(id: string): Promise<Game | undefined> {
    if (!this.db) return;
    return this.db.get('games', id);
  }

  async getCachedPlayers(): Promise<Player[]> {
    if (!this.db) return [];
    return this.db.getAll('players');
  }

  async getCachedPitches(gameId: string): Promise<PitchCount[]> {
    if (!this.db) return [];
    const all = await this.db.getAll('pitches');
    return all.filter((p) => p.game_id === gameId);
  }

  // Cache pitch rules for offline access
  async cachePitchRules(rules: PitchRule[]): Promise<void> {
    if (!this.db) return;
    const tx = this.db.transaction('pitch-rules', 'readwrite');
    await tx.store.clear();
    await Promise.all(rules.map((rule) => tx.store.put(rule)));
    await tx.done;
  }

  async getCachedPitchRules(): Promise<PitchRule[]> {
    if (!this.db) return [];
    return this.db.getAll('pitch-rules');
  }

  async getCachedPitchRuleByAgeGroup(ageGroup: string): Promise<PitchRule | undefined> {
    if (!this.db) return undefined;
    const rules = await this.db.getAll('pitch-rules');
    return rules.find((r) => r.age_group === ageGroup);
  }

  // Cache rest status for players
  async cachePlayerRestStatuses(statuses: RestStatus[]): Promise<void> {
    if (!this.db) return;
    const tx = this.db.transaction('player-rest-status', 'readwrite');
    await Promise.all(statuses.map((s) => tx.store.put(s)));
    await tx.done;
  }

  async getCachedRestStatus(playerId: string): Promise<RestStatus | undefined> {
    if (!this.db) return undefined;
    return this.db.get('player-rest-status', playerId);
  }

  async getAllCachedRestStatuses(): Promise<RestStatus[]> {
    if (!this.db) return [];
    return this.db.getAll('player-rest-status');
  }

  async clearRestStatusCache(): Promise<void> {
    if (!this.db) return;
    const tx = this.db.transaction('player-rest-status', 'readwrite');
    await tx.store.clear();
    await tx.done;
  }

  // Pre-cache data when starting a game for offline support
  async prepareForOfflineGame(gameId: string, teamId: string): Promise<void> {
    if (!this.state.isOnline) {
      console.warn('Cannot prepare offline cache while offline');
      return;
    }

    try {
      const [rules, availability] = await Promise.all([
        api.getDefaultPitchRules(),
        api.getTeamPitcherAvailability(teamId),
      ]);

      await this.cachePitchRules(rules);
      await this.cachePlayerRestStatuses(availability);

      console.log(`Cached ${rules.length} pitch rules and ${availability.length} rest statuses for offline game`);
    } catch (err) {
      console.warn('Failed to cache offline data:', err);
    }
  }

  // Check if we have cached data for offline mode
  async hasOfflineData(): Promise<boolean> {
    if (!this.db) return false;
    const rulesCount = await this.db.count('pitch-rules');
    return rulesCount > 0;
  }
}

export const offlineService = new OfflineService();
